import { createTheme } from '@mui/material/styles';

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    'transparent-text-blue-with-blue-border': true;
    'transparent-text-blue': true;
    'transparent-text-red-with-red-border': true;
  }
}

export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#009DE0',
      dark: '#0087C3',
      light: '#33B1E6',
      contrastText: '#fff',
    },
    secondary: {
      main: '#002A5C',
      dark: '#001F45',
      light: '#1A3D6E',
    },
    error: { main: '#E30613' },
    success: { main: '#2E7D32' },
    warning: { main: '#FFB800' },
    background: {
      default: '#F5F7FA',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#002A5C',
      secondary: '#5C6B7A',
    },
    divider: '#DEE3EA',
  },
  typography: {
    fontFamily: '"DM Sans", "Helvetica Neue", Arial, sans-serif',
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: { borderRadius: 8 },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(90deg, #0087C3 0%, #009DE0 60%, #33B1E6 100%)',
          color: '#fff',
          boxShadow: '0 2px 8px rgba(0,42,92,0.18)',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: '3px 3px 0 0',
          backgroundColor: '#009DE0',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.85rem',
          minHeight: 44,
          color: '#5C6B7A',
          '&.Mui-selected': { color: '#002A5C' },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          marginLeft: 8,
          marginRight: 8,
          marginTop: 2,
          marginBottom: 2,
          transition: 'background-color 120ms ease, box-shadow 120ms ease',
          '&:hover': { backgroundColor: '#EDF1F7' },
          '&.Mui-selected': {
            backgroundColor: 'rgba(0,157,224,0.10)',
            boxShadow: 'inset 3px 0 0 #009DE0',
            '&:hover': { backgroundColor: 'rgba(0,157,224,0.15)' },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: '#FFFFFF',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#DEE3EA' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#B0BAC5' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#009DE0', borderWidth: 1 },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '999px',
          boxShadow: 'none',
          textTransform: 'none',
          fontWeight: 600,
          paddingLeft: '20px',
          paddingRight: '20px',
          paddingTop: '4px',
          paddingBottom: '4px',
          '&:hover': { boxShadow: 'none' },
        },
      },
      variants: [
        {
          props: { variant: 'contained', color: 'primary' },
          style: {
            backgroundColor: '#009DE0',
            '&:hover': { backgroundColor: '#0087C3' },
          },
        },
        {
          props: { variant: 'transparent-text-blue-with-blue-border' },
          style: {
            border: '1px solid #009DE0',
            borderColor: '#009DE0',
            color: '#009DE0',
            backgroundColor: 'transparent !important',
            '&:hover': {
              backgroundColor: 'rgba(0, 157, 224, 0.06) !important',
              borderColor: '#009DE0',
            },
          },
        },
        {
          props: { variant: 'transparent-text-blue' },
          style: {
            color: '#009DE0',
            backgroundColor: 'transparent !important',
            '&:hover': {
              backgroundColor: 'rgba(0, 157, 224, 0.06) !important',
            },
          },
        },
        {
          props: { variant: 'transparent-text-red-with-red-border' },
          style: {
            border: '1px solid #E30613',
            borderColor: '#E30613',
            color: '#E30613',
            backgroundColor: 'transparent !important',
            '&:hover': {
              backgroundColor: 'rgba(227, 6, 19, 0.04) !important',
              borderColor: '#E30613',
            },
          },
        },
      ],
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          boxShadow: '0 1px 3px rgba(0,42,92,0.08), 0 1px 2px rgba(0,42,92,0.04)',
          border: '1px solid #EEF1F5',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '999px',
          fontWeight: 600,
          fontSize: '0.72rem',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: '#DEE3EA' },
      },
    },
  },
});
